/**
 * 검수 "의심 신호(Suspicion Signals)" 공통 검출 (M21 #3 공통 기반).
 *
 * 여기서 계산한 신호는 다음에서 재사용된다.
 *   - #3 편집 화면 필드 강조 (빈값/불일치)
 *   - #4 "확인 필요" 소견 리스트 (우측 상단)
 *   - #5 추천질문 자동 생성 입력
 *   - #7 최종 AI 검토 입력
 *
 * 참고: 신뢰도(S1, confidence<1.0)는 필드별 ``csv_bbox`` 로 이미 판단하므로
 * 여기서는 값 기반 신호(S2~S4)만 다룬다.
 */

import type { EditableFieldKey } from "./fieldLabels";

export type ReviewSignalKind =
  | "code_name_mismatch" // S2: 현지대리인코드 대리인명 ↔ 파일명 표기 대리인명 불일치
  | "swift_and_withholding_empty" // S3: SWIFT + 원천징수액 동시 공란
  | "billing_party_empty"; // S4: 請求先(청구처) 공란

export interface ReviewSignal {
  kind: ReviewSignalKind;
  /** 강조 대상 필드들. */
  fields: EditableFieldKey[];
  /** 소견 리스트/툴팁용 i18n 메시지 키. */
  messageKey: string;
}

function isEmpty(v: unknown): boolean {
  return v == null || String(v).trim() === "";
}

/** 영숫자만 남기고 소문자화 (대리인명 ↔ 파일명 대조용). */
function normalizeName(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * 값 기반 의심 신호(S2~S4) 계산.
 *
 * @param csv        현재 편집 중인 값 (record.csv_data 또는 편집 상태)
 * @param documentId 파일명 (PDF名 / original_name)
 */
export function computeReviewSignals(
  csv: Record<string, unknown>,
  documentId: string,
): ReviewSignal[] {
  const signals: ReviewSignal[] = [];

  // S4 — 請求先(청구처) 공란: 주요 반려 포인트.
  if (isEmpty(csv.billing_party)) {
    signals.push({
      kind: "billing_party_empty",
      fields: ["billing_party"],
      messageKey: "records.signals.billingPartyEmpty",
    });
  }

  // S3 — SWIFT + 원천징수액 동시 공란: 정상 케이스에선 둘 다 비는 경우 없음.
  if (isEmpty(csv.swift_code) && isEmpty(csv.withholding_tax)) {
    signals.push({
      kind: "swift_and_withholding_empty",
      fields: ["swift_code", "withholding_tax"],
      messageKey: "records.signals.swiftWithholdingEmpty",
    });
  }

  // S2 — 코드 대리인명 ↔ 파일명 표기 대리인명 불일치 (이중코드 자동 검출).
  //   agent_name 은 마스터 공식명(현지대리인코드 기준). 파일명에 그 이름의
  //   접두(prefix 5자)가 등장하지 않으면 불일치로 본다.
  const nameNorm = normalizeName(String(csv.agent_name ?? ""));
  const fileNorm = normalizeName(documentId.replace(/\.[^.]+$/, ""));
  if (nameNorm.length >= 3 && fileNorm.length > 0) {
    const probe = nameNorm.slice(0, Math.min(5, nameNorm.length));
    if (!fileNorm.includes(probe)) {
      signals.push({
        kind: "code_name_mismatch",
        fields: ["local_agent_code", "agent_name"],
        messageKey: "records.signals.codeNameMismatch",
      });
    }
  }

  return signals;
}

/** 신호 목록에서 강조 대상 필드 → 메시지 키 맵 (첫 신호 우선). */
export function fieldSignalMap(signals: ReviewSignal[]): Map<EditableFieldKey, string> {
  const map = new Map<EditableFieldKey, string>();
  for (const sig of signals) {
    for (const f of sig.fields) {
      if (!map.has(f)) map.set(f, sig.messageKey);
    }
  }
  return map;
}
